import { allocate, mulRate, sum, type Money } from "../money/money";

/**
 * Promotional percentage discounts — the "20% for a friend" case (FR-032).
 *
 * WHY THIS IS SPREAD ACROSS THE LINES rather than subtracted from the total.
 *
 * `priceCart` accepts a sale-level discount, and taking one look at the
 * arithmetic that is plainly the simpler option: subtract once at the end. It
 * is also wrong in a way that costs real money, and only shows up later.
 *
 * A refund is computed from `lineTotal`. Leave the discount at sale level and
 * every line keeps its FULL price while the customer paid the discounted total.
 * Refund one line of a 20%-off sale and the till hands back ₱100 for an item
 * that was sold for ₱80. Refund the whole sale line by line and the cumulative
 * cap rejects it outright, because the lines now add up to more than the sale.
 * One of those loses money quietly; the other strands a cashier in front of a
 * customer. Allocating the discount onto the lines makes `lineTotal` mean what
 * it says — what was actually charged for that line — and both problems stop
 * existing rather than needing to be handled.
 *
 * The split uses largest-remainder allocation, so the parts sum to EXACTLY the
 * discount. Rounding each line independently would lose or invent a centavo,
 * and a receipt whose lines do not add up to its total is a defect no customer
 * should have to notice on our behalf.
 */

/** 100% off. A discount may reach it, and may never exceed it. */
export const MAX_RATE_BP = 10_000;

export class DiscountError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DiscountError";
  }
}

export interface DiscountableLine {
  lineId: string;
  /** (unit price + modifiers) × quantity, before any discount. */
  lineSubtotal: Money;
}

export interface LineDiscount {
  lineId: string;
  amount: Money;
}

/** Percent as basis points: 20% is 2000. Rejects anything unrepresentable. */
export function rateFromPercent(percent: number): number {
  if (!Number.isFinite(percent)) {
    throw new DiscountError(`Percentage must be a number, got ${percent}`);
  }

  const rateBp = Math.round(percent * 100);

  if (rateBp <= 0) {
    throw new DiscountError(`A discount must be greater than 0%, got ${percent}%`);
  }
  if (rateBp > MAX_RATE_BP) {
    throw new DiscountError(`A discount cannot exceed 100%, got ${percent}%`);
  }

  return rateBp;
}

/**
 * Split a percentage discount across the lines it applies to.
 *
 * Weighted by each line's subtotal, so a ₱300 line absorbs three times the
 * reduction of a ₱100 line — which is what a percentage off the order means.
 */
export function allocatePromoDiscount(
  lines: readonly DiscountableLine[],
  rateBp: number,
): LineDiscount[] {
  if (!Number.isInteger(rateBp) || rateBp <= 0 || rateBp > MAX_RATE_BP) {
    throw new DiscountError(`Rate must be whole basis points in 1..${MAX_RATE_BP}, got ${rateBp}`);
  }
  if (lines.length === 0) return [];

  const subtotal = sum(lines.map((l) => l.lineSubtotal));
  if (subtotal <= 0) return lines.map((l) => ({ lineId: l.lineId, amount: 0 as Money }));

  const total = mulRate(subtotal, rateBp);
  const parts = allocate(
    total,
    lines.map((l) => l.lineSubtotal),
  );

  return lines.map((line, index) => ({
    lineId: line.lineId,
    amount: parts[index] ?? (0 as Money),
  }));
}

/** What the customer comes off, in total, at this rate. */
export function promoDiscountTotal(
  lines: readonly DiscountableLine[],
  rateBp: number,
): Money {
  return sum(allocatePromoDiscount(lines, rateBp).map((d) => d.amount));
}
